import { z } from 'zod';
import { PassportIconSchema } from '../constants/passport-icons';
// Input: login por email (sin contraseña)
export const PassportLoginInput = z.object({
    email: z.string().email(),
});
// Output del login: userId + email
export const PassportLoginOutput = z.object({
    userId: z.string().uuid(),
    email: z.string().email(),
});
/**
 * Código corto del ponente (PassportActivity de tipo TALK).
 *
 * Pensado para dictarse de viva voz desde tarima: 4 caracteres A-Z0-9
 * (36^4 = 1.6M combinaciones, holgado para decenas de TALKs por evento).
 * El alfabeto/longitud son distintos a `ShortCodeSchema` de Attendee (6
 * chars) — no reusarlos para no diluir la unicidad de Attendee.shortCode.
 */
export const SPEAKER_SHORT_CODE_LENGTH = 4;
export const SPEAKER_SHORT_CODE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
export const SpeakerShortCodeSchema = z.string().regex(/^[A-Z0-9]{4}$/);
/**
 * Input: escanear QR de actividad o capturar el código corto del ponente.
 * Union para que el mismo endpoint sirva a la cámara y al input manual.
 */
export const PassportScanInput = z.union([
    z.object({ userId: z.string().uuid(), token: z.string().uuid() }),
    z.object({ userId: z.string().uuid(), shortCode: SpeakerShortCodeSchema }),
]);
// ---------------------------------------------------------------------------
// Tipos de actividad y DTOs nuevos (refactor secciones + actividades)
// ---------------------------------------------------------------------------
export const PassportActivityTypeSchema = z.enum(['TALK', 'SECONDARY', 'SPONSOR']);
/**
 * Tipos de actividad que se desbloquean por QR y cuentan para el progreso
 * global y el certificado. SECONDARY queda fuera (es checklist manual).
 */
export const CERTIFIABLE_ACTIVITY_TYPES = ['TALK', 'SPONSOR'];
export function isCertifiableActivity(type) {
    return CERTIFIABLE_ACTIVITY_TYPES.includes(type);
}
// ---------------------------------------------------------------------------
// Recursos por actividad
// ---------------------------------------------------------------------------
export const PassportResourceDto = z.object({
    id: z.string().uuid(),
    activityId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    url: z.string().url(),
    order: z.number().int(),
});
export const PassportResourceInput = z.object({
    activityId: z.string().uuid(),
    title: z.string().min(1).max(200),
    description: z.string().max(500).optional(),
    url: z.string().url(),
    order: z.number().int().optional(),
});
export const PassportResourceUpdateInput = PassportResourceInput.omit({
    activityId: true,
}).partial();
// DTO actividad para asistente (con unlockedAt)
export const PassportActivityDto = z.object({
    id: z.string().uuid(),
    type: PassportActivityTypeSchema,
    title: z.string(),
    speakerName: z.string().nullable(),
    speakerBio: z.string().nullable(),
    speakerPhotoUrl: z.string().nullable(),
    description: z.string().nullable(),
    icon: PassportIconSchema.nullable(),
    order: z.number().int(),
    unlockedAt: z.string().datetime().nullable(),
    resources: z.array(PassportResourceDto),
});
// DTO sección con sus actividades agrupadas
export const PassportSectionDto = z.object({
    id: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    icon: PassportIconSchema.nullable(),
    order: z.number().int(),
    activities: z.array(PassportActivityDto),
});
// Perfil de Attendee (read-only, join por email)
export const AttendeeProfileDto = z.object({
    fullName: z.string(),
    phone: z.string(),
    profesion: z.string().nullable(),
    empresa: z.string().nullable(),
    qrToken: z.string().uuid(),
});
// DTO pasaporte completo
export const PassportDto = z.object({
    userId: z.string().uuid(),
    email: z.string().email(),
    attendeeProfile: AttendeeProfileDto.nullable(),
    sections: z.array(PassportSectionDto),
});
// Output del scan — "activity" reemplaza al legacy "session"
export const PassportScanOutput = z.object({
    result: z.enum(['OK', 'DUPLICATE', 'INVALID']),
    activity: PassportActivityDto.nullable(),
    message: z.string(),
});
// ---------------------------------------------------------------------------
// Admin — Sections CRUD
// ---------------------------------------------------------------------------
export const PassportSectionInput = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(500).optional(),
    icon: PassportIconSchema.optional(),
    order: z.number().int().optional(),
});
export const PassportSectionUpdateInput = PassportSectionInput.partial();
// ---------------------------------------------------------------------------
// Admin — Activities CRUD
// ---------------------------------------------------------------------------
export const PassportActivityInput = z
    .object({
    sectionId: z.string().uuid(),
    type: PassportActivityTypeSchema.optional(),
    title: z.string().min(1).max(200),
    speakerName: z.string().max(200).optional(),
    speakerEmail: z.string().email().optional(),
    speakerBio: z.string().max(1000).optional(),
    description: z.string().max(500).optional(),
    icon: PassportIconSchema.optional(),
    order: z.number().int().optional(),
})
    .refine((v) => !['TALK', 'SPONSOR'].includes(v.type ?? 'TALK') || !!v.speakerName?.trim(), {
    message: 'speakerName es obligatorio para TALK y SPONSOR',
    path: ['speakerName'],
});
export const PassportActivityUpdateInput = z.object({
    type: PassportActivityTypeSchema.optional(),
    title: z.string().min(1).max(200).optional(),
    speakerName: z.string().max(200).nullable().optional(),
    speakerEmail: z.string().email().nullable().optional(),
    speakerBio: z.string().max(1000).nullable().optional(),
    description: z.string().max(500).nullable().optional(),
    icon: PassportIconSchema.nullable().optional(),
    order: z.number().int().optional(),
    sectionId: z.string().uuid().optional(),
});
// Activity admin DTO
export const PassportActivityAdminDto = z.object({
    id: z.string().uuid(),
    sectionId: z.string().uuid(),
    type: PassportActivityTypeSchema,
    title: z.string(),
    speakerName: z.string().nullable(),
    speakerEmail: z.string().nullable(),
    speakerBio: z.string().nullable(),
    speakerPhotoUrl: z.string().nullable(),
    description: z.string().nullable(),
    icon: PassportIconSchema.nullable(),
    order: z.number().int(),
    qrToken: z.string().uuid(),
    shortCode: SpeakerShortCodeSchema.nullable(),
    entryCount: z.number().int(),
    resources: z.array(PassportResourceDto),
});
// Section admin DTO (con actividades anidadas)
export const PassportSectionAdminDto = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    icon: PassportIconSchema.nullable(),
    order: z.number().int(),
    activities: z.array(PassportActivityAdminDto),
});
// ---------------------------------------------------------------------------
// Admin — Events
// ---------------------------------------------------------------------------
export const PassportEventInput = z.object({
    title: z.string().min(1).max(200),
    date: z.string().datetime().optional(),
    ticketingEventId: z.string().uuid(),
});
export const PassportEventDto = z.object({
    id: z.string().uuid(),
    ticketingEventId: z.string().uuid(),
    title: z.string(),
    date: z.string().datetime().nullable(),
    sectionCount: z.number().int(),
    activityCount: z.number().int(),
    createdAt: z.string().datetime(),
});
export const PassportEventListDto = z.array(PassportEventDto);
export const PassportEventDetailDto = z.object({
    id: z.string().uuid(),
    ticketingEventId: z.string().uuid(),
    title: z.string(),
    date: z.string().datetime().nullable(),
    sectionCount: z.number().int(),
    activityCount: z.number().int(),
    createdAt: z.string().datetime(),
    sections: z.array(PassportSectionAdminDto),
});
// ---------------------------------------------------------------------------
// Asistente — resumen de eventos con progreso
// ---------------------------------------------------------------------------
export const PassportEventSummaryDto = z.object({
    id: z.string().uuid(),
    ticketingEventId: z.string().uuid(),
    title: z.string(),
    date: z.string().datetime().nullable(),
    totalActivities: z.number().int(),
    unlockedActivities: z.number().int(),
});
export const PassportEventSummaryListDto = z.array(PassportEventSummaryDto);
// ---------------------------------------------------------------------------
// Session info (deep-link stamp?token=) — el nombre queda por compatibilidad
// ---------------------------------------------------------------------------
export const PassportSessionInfoDto = z.object({
    activityId: z.string().uuid(),
    title: z.string(),
    speakerName: z.string().nullable(),
    eventId: z.string().uuid(),
    eventTitle: z.string(),
});
// ---------------------------------------------------------------------------
// Upload de foto del ponente/patrocinador
// ---------------------------------------------------------------------------
export const PassportPhotoUploadDto = z.object({
    photoUrl: z.string(),
});
export const PassportActivityAdminDetailDto = PassportActivityAdminDto.extend({
    eventId: z.string().uuid(),
    eventTitle: z.string(),
});
// ---------------------------------------------------------------------------
// Toggle de actividad manual (SECONDARY / SPONSOR)
// ---------------------------------------------------------------------------
export const PassportToggleActivityInput = z.object({
    userId: z.string().uuid(),
    activityId: z.string().uuid(),
});
export const PassportToggleActivityOutput = z.object({
    checked: z.boolean(),
});
// ---------------------------------------------------------------------------
// Admin — Sponsors CRUD
// ---------------------------------------------------------------------------
export const PassportSponsorInput = z.object({
    name: z.string().min(1).max(200),
    description: z.string().min(1).max(1000),
    fit: z.enum(['cover', 'contain']).default('cover'),
    order: z.number().int().optional(),
});
export const PassportSponsorUpdateInput = PassportSponsorInput.partial();
export const PassportSponsorDto = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    logoUrl: z.string().nullable(),
    fit: z.enum(['cover', 'contain']),
    order: z.number().int(),
});
export const PassportSponsorListDto = z.array(PassportSponsorDto);
// ---------------------------------------------------------------------------
// Admin — Logs de escaneos de actividades (speakers / sponsors)
// ---------------------------------------------------------------------------
export const ActivityLogsQuery = z.object({
    type: PassportActivityTypeSchema.optional(),
    activityId: z.string().uuid().optional(),
    eventId: z.string().uuid().optional(),
    userEmail: z.string().trim().min(1).optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(50),
});
export const ActivityLogItem = z.object({
    id: z.string().uuid(),
    scannedAt: z.string().datetime(),
    activity: z.object({
        id: z.string().uuid(),
        type: PassportActivityTypeSchema,
        title: z.string(),
        speakerName: z.string().nullable(),
    }),
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
    }),
});
export const ActivityLogsResponse = z.object({
    items: z.array(ActivityLogItem),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
//# sourceMappingURL=passport.js.map